// ============================================
// Story Manager — replaces Unity InkManager.cs
// Manages inkjs story lifecycle, content flow,
// choice selection, forced events, dead-end recovery
// ============================================

import { events, Events } from './events.js';
import { Stats } from './constants.js';
import { tagHandler } from './tags.js';
import { variableManager } from './variables.js';
import { L } from '../systems/localization.js';
import { bindExternals, getEffectiveFemGain, getEffectiveSubGain, getEffectiveShameGain,
         getPassiveArousalGain, getPassiveSubGain, getArousalPenalty, getComfortDiscount,
         femGainPacingSuppressed } from './externals.js';

/**
 * Short stat labels for the positive-framed disabled-choice reason.
 * Falls back to the stat's raw name if no short form is known. Kept
 * locally to avoid circular import with ui/choices.js.
 */
function _shortStatLabel(stat) {
    const short = {
        'Femininity': L.get('stat.fem_short') !== 'stat.fem_short' ? L.get('stat.fem_short') : 'Fem',
        'Submissiveness': L.get('stat.sub_short') !== 'stat.sub_short' ? L.get('stat.sub_short') : 'Sub',
        'Arousal': L.get('stat.arousal_short') !== 'stat.arousal_short' ? L.get('stat.arousal_short') : 'Arousal',
        'Shame': 'Shame',
        'Money': L.get('stat.money') !== 'stat.money' ? L.get('stat.money') : 'Money',
        'Energy': 'Energy',
        'Masculinity': 'Masc',
    };
    return short[stat] || stat;
}

/**
 * Group multi-knot narrative arcs under one logical location for the
 * purpose of clear-on-location-change detection. Returning the same
 * value for two adjacent currentLocation values means the scroll-append
 * panel is NOT cleared between them, so the player can scroll back to
 * read the previous beat. Knot names that don't share a family are
 * returned unchanged. Add new families here when shipping a multi-knot
 * scene that should read as one continuous block.
 */
// F16: knot families are data-driven so new multi-knot narrative arcs (Endgame,
// long Dating sequences, Sarah-Confrontation aftermath) can be added without
// touching engine code. Each entry: prefix to match (knot must equal `prefix`
// or start with `prefix + '_'`), and the canonical family name returned to
// the caller.
const KNOT_FAMILIES = [
    { prefix: 'Prologue', name: 'Prologue' },
    // BNWO arc (0.3): University_Entry + all BNWO_* hub/stage/room knots are one
    // continuous campus scene — group so intra-arc navigation keeps scrollback.
    { prefix: 'University', name: 'University' },
    { prefix: 'BNWO', name: 'University' },
    // Endgame chains share a single scrollback narrative beat — adding here
    // keeps the player's "final-night" flow continuous in story panel.
    { prefix: 'Endgame_KeptSissy', name: 'Endgame_KeptSissy' },
    { prefix: 'Endgame_Independent', name: 'Endgame_Independent' },
    // Sarah confrontation/aftermath spans 2-3 plain-divert knots; group so
    // the dramatic beat doesn't lose scrollback mid-scene.
    { prefix: 'Sarah_Confrontation', name: 'Sarah_Confrontation' },
    // Boyfriend-route mirror (0.3.1 BOYFRIEND_START): Jesse confrontation
    // spans the same plain-divert chain shape as Sarah's.
    { prefix: 'Jesse_Confrontation', name: 'Jesse_Confrontation' },
    // Morning hub family: overnight_morning_events → check_dreams →
    // check_milestones → check_endgame → MorningRoutine all stack narration
    // into one continuous block, and MorningRoutine_Shower / _Makeup /
    // _CagePost / _Wardrobe / _Skip return to MorningRoutine without
    // wiping the panel mid-flow. The player reads the morning beat as
    // ONE scene from sleep to "Begin day".
    //
    // Apartment is its OWN family (separate from MorningRoutineHub). The
    // transition MorningRoutine_Skip → Apartment is a different family,
    // which triggers a clean STORY_CLEAR so the Apartment hub starts
    // fresh — the morning narration was already consumed via the
    // Continue-gate ack, no need to keep it on the hub screen.
    //
    // Sleep knots (`ApartmentSleep`, `ApartmentSleep_Forced`, `LoftSleep`)
    // are grouped INTO MorningRoutineHub by the explicit entries below
    // (foundby:user 2026-05-20), so the overnight cards survive the
    // transition into the morning recap instead of being wiped.
    { prefix: 'MorningRoutine', name: 'MorningRoutineHub' },
    { prefix: 'LoftMorningRoutine', name: 'MorningRoutineHub' },
    // Overnight chain knots all belong to MorningRoutineHub so dream +
    // milestone + SMS narration survives the transition into the recap
    // card + Continue gate. Pre-fix (foundby:user 2026-05-20) the family-
    // clear race wiped every overnight card before the MorningRoutine knot
    // entered, so the player only saw "It's a new morning" with no scroll-
    // back of the events that just happened.
    { prefix: 'ApartmentSleep', name: 'MorningRoutineHub' },
    { prefix: 'LoftSleep', name: 'MorningRoutineHub' },
    { prefix: 'overnight_processing', name: 'MorningRoutineHub' },
    { prefix: 'overnight_morning_events', name: 'MorningRoutineHub' },
    { prefix: 'check_dreams', name: 'MorningRoutineHub' },
    { prefix: 'check_milestones', name: 'MorningRoutineHub' },
    { prefix: 'check_endgame', name: 'MorningRoutineHub' },
    { prefix: 'check_independent_arc', name: 'MorningRoutineHub' },
    { prefix: 'Dream', name: 'MorningRoutineHub' },
    { prefix: 'Milestone', name: 'MorningRoutineHub' },
    // NOTE: prefixes must NOT carry a trailing underscore — the matcher
    // tests `loc === prefix || loc.startsWith(prefix + '_')`, so a prefix
    // like '_AS_after_' built '_AS_after__' and never matched anything
    // (dead entry shipped 2026-05-20, caught in the v0.2.8 § 22 pass).
    { prefix: '_AS_after', name: 'MorningRoutineHub' },
    { prefix: '_ASF_after', name: 'MorningRoutineHub' },
    { prefix: '_Loft_after', name: 'MorningRoutineHub' },
    // Milestone-cascade continuation stubs (_CM_after_*, _CM_resume, …).
    { prefix: '_CM', name: 'MorningRoutineHub' },
    // Morning-events continuation stubs + exit dispatcher (§ 22 v0.2.8:
    // _OME_after_beer/_rent/_notice/_offer/_firstnight/_itch/_public/
    // _acceptance/_keyrelease, _OME_exit).
    { prefix: '_OME', name: 'MorningRoutineHub' },
    { prefix: '_morning_resume', name: 'MorningRoutineHub' },
    { prefix: 'Apartment', name: 'ApartmentHub' },
    { prefix: 'Loft', name: 'ApartmentHub' },
];

function _locationFamily(loc) {
    if (!loc) return loc;
    for (const fam of KNOT_FAMILIES) {
        if (loc === fam.prefix || loc.startsWith(fam.prefix + '_')) return fam.name;
    }
    return loc;
}

/**
 * Location map: Constants.Location -> Ink knot name
 */
const LOCATION_TO_KNOT = {
    'Home':    'Apartment',
    'Bar':     'BarEntry',
    'Park':    'ParkEntry',
    'Mall':    'MallEntry',
    'Outside': 'Outside',
    'Salon':   'SalonEntry',
    'Beach':   'BeachEntry',
    'Club':    'ClubEntry',
    'Gym':     'GymEntry',
    'Clinic':  'ClinicEntry',
    'Spa':     'SpaEntry',
    'Office':  'Office_Entry',
    'Maid':    'Maid_Entry',
    'Downtown': 'Downtown',
    'CornerStore': 'CornerStoreEntry',
};

class StoryManager {
    constructor() {
        /** @type {object|null} inkjs Story instance */
        this.currentStory = null;

        /** @type {string} Current top-level knot name */
        this.currentLocation = '';

        /** @type {string|null} Location key from last changeLocation() call */
        this.travelLocation = null;

        /** @type {string} Last BGM value — used as fallback location hint */
        this.lastBGM = '';

        /** @type {boolean} True while processing story content */
        this.isProcessing = false;

        /** @type {boolean} True when waiting for external input (shop/wardrobe) */
        this.waitingForInput = false;

        // Speaker state
        this._currentSpeaker = 'info';
        this._currentPortrait = '';

        // Forced event state (SMS [goto:] system)
        this._savedKnotPath = null;
        this._preEventVarSnapshot = null;
        this._inForcedEvent = false;
        this._forcedEventReturning = false;

        // Last emitted choices (for hotkey access)
        this._lastChoices = null;

        // Break delay state
        this._pendingDelay = 0;

        // P0504: Per-knot narration cache for empty-panel-with-choices recovery.
        // Map<knot, Array<{type:'info'|'dialogue', speaker, text, lineId}>>.
        // Populated in _createContentView, cleared per-knot in _clearContentView.
        // Used by the watchdog to replay missing narration when story-content
        // ends up empty but choices are ready (forced-event return paths,
        // overlay race conditions, anything that wipes the panel without
        // re-rendering — see VelvetLounge_ApplyJob report 2026-05-03 Moralesss).
        this._narrationByKnot = new Map();
        this._narrationReplayInProgress = false;

        // Undo-button: in-memory ring buffer of pre-choice snapshots.
        // Each entry mirrors a savegame payload (inkState + variableManager
        // exportState) plus the JS-side forced-event flags + DOM HTML, so
        // restore is byte-equivalent to load. Capped at 5; pushed in
        // selectChoice() before ChooseChoiceIndex; popped by undoLastChoice().
        // Skipped while bar/streaming minigame is active (their UI carries
        // local JS state Undo can't re-hydrate).
        this._undoStack = [];
        this._undoMax = 5;
        // Undo state providers — JS-side managers whose state is NOT covered
        // by inkState + variableManager (inventory equips via #item: tags,
        // phone threads/app installs). Registered from main.js (avoids the
        // engine→ui import cycle). Without these, undo across a purchase or
        // #sms: left the managers in the future while Ink rewound — the cage
        // "resurrected" on the next recalc push and the StateValidator fired
        // chastity_flag_no_tier (foundby:Diadan 0.3.0.3).
        this._undoStateProviders = [];
        // Set transiently by undoLastChoice() so the next refreshView skips
        // STORY_APPEND — DOM was restored exactly to pre-choice state.
        this._suppressNextAppend = false;

        // F24: scope listeners through createScope so test harnesses can
        // tear down + re-instantiate the storyManager without listener
        // accumulation. Production singleton survives session-long, so
        // destroy() is rarely called — but the scope-tagged callbacks now
        // also surface their origin in emit()'s error log (via _scopeLabel).
        this._scope = events.createScope('storyManager');
        this._scope.on(Events.TAG_CHAR, (value) => this._onCharacterTag(value));
        this._scope.on(Events.TAG_IMAGE, (value) => {
            events.emit(Events.STORY_IMAGE, value);
            // P0504-extension: also cache for empty-panel replay so recovery
            // re-shows the scene image, not just text. Without this the
            // user reports "text appears but pictures are missing" after a
            // forced-event return / save-load drift triggers replay.
            this._recordNarrationCard({ type: 'image', value });
        });
        this._scope.on(Events.TAG_VIDEO, (value) => {
            events.emit(Events.STORY_VIDEO, value);
            this._recordNarrationCard({ type: 'video', value });
        });
        this._scope.on(Events.TAG_RETURN, (value) => {
            if (value === 'abort') this._abortForcedEvent();
            else this._onReturnFromForcedEvent();
        });
        this._scope.on(Events.TAG_BREAK, (delay) => { this._pendingDelay = delay; });
        // Track whether a #ui: tag fired this refresh — see _processStoryContent.
        // Empty value ('') from clearUIMode itself should NOT count, only
        // genuine knot-level #ui:foo emissions.
        this._scope.on(Events.TAG_UI, (mode) => {
            if (mode) this._uiTagFiredThisRefresh = true;
        });

        // Re-translate choices when language changes mid-game
        this._scope.on(Events.LOCALE_CHANGED, () => {
            if (this._lastChoices && this.currentStory) {
                this._createChoicesUI();
            }
        });

        // Undo: clear stack on new game / load — old snapshots reference
        // a story state that no longer matches.
        this._scope.on(Events.GAME_NEW, () => this._clearUndoStack());
        this._scope.on(Events.GAME_LOADED, () => this._clearUndoStack());
    }

    /** Test-only: tear down all listeners. Production never calls this. */
    destroy() {
        if (this._scope) { this._scope.destroy(); this._scope = null; }
        if (this._delayTimer) { try { clearTimeout(this._delayTimer); } catch(_) {} this._delayTimer = null; }
    }

    // ------------------------------------------------------------------
    // Public API: Initialization
    // ------------------------------------------------------------------

    /**
     * @returns {boolean} Whether currently in a forced event
     */
    get inForcedEvent() { return this._inForcedEvent; }

    /**
     * Initialize story from compiled Ink JSON. Does NOT start processing.
     * Use before loadGame(), then call refreshView() yourself.
     * @param {string} storyJson - Compiled Ink JSON string
     */
    initStory(storyJson) {
        // inkjs must be loaded globally (via <script> tag or import)
        if (typeof inkjs === 'undefined') {
            throw new Error('[StoryManager] inkjs library not loaded');
        }

        this.currentStory = new inkjs.Story(storyJson);

        // Set unique random seed per playthrough
        try {
            this.currentStory.variablesState['game_seed'] = Date.now() % 100000;
        } catch (e) {
            console.error('[StoryManager] game_seed not found — Ink JSON may be stale');
        }

        // Bind external functions
        bindExternals(this.currentStory);

        // Hook variable manager observers
        variableManager.initHookStoryEvents(this.currentStory);

        // Give tag handler access to story
        tagHandler.setStory(this.currentStory);

        // Register error handler
        this.currentStory.onError = (msg, type) => {
            if (type === 1) console.warn(`[Ink] ${msg}`);
            else console.error(`[Ink] ${msg}`);
        };
    }

    /**
     * Start the story fresh (new game). Pushes variables and begins processing.
     * @param {string} storyJson - Compiled Ink JSON string
     */
    startStory(storyJson) {
        this.isProcessing = false;
        this.waitingForInput = false;

        this.initStory(storyJson);
        variableManager.pushVariablesToInk(this.currentStory);
        this._forceClearNext = true; // New game = full clear
        this.refreshView(true);
    }

    /**
     * Start story from a saved state. Call initStory() first, then loadGame(),
     * then this method.
     * @param {boolean} [hasSave=false] - Whether a save was loaded
     */
    startFromSave(hasSave = false) {
        variableManager.pushVariablesToInk(this.currentStory);

        // Dead-end recovery: if save was captured mid-choice
        if (hasSave && !this.currentStory.canContinue && this.currentStory.currentChoices.length === 0) {
            console.warn('[StoryManager] Save at dead-end — recovering to Apartment');
            try { this.currentStory.ChoosePathString('Apartment'); }
            catch (e) { console.error(`[StoryManager] Recovery failed: ${e.message}`); }
        }

        this._forceClearNext = true; // Load = full clear
        // Skip autosave when we just loaded
        this.refreshView(!hasSave);
    }

    // ------------------------------------------------------------------
    // Public API: Story Flow
    // ------------------------------------------------------------------

    /**
     * Refresh the story view: append new content (or clear on location change), present choices.
     * @param {boolean} [saveBeforeRefresh=true] - Auto-save before processing
     */
    refreshView(saveBeforeRefresh = true) {
        if (this.isProcessing || !this.currentStory) return;

        // RACE-FREE media stop (v0.2.9). Synchronous, FIRST thing every advance
        // does — before _clearContentView and before the async _processStoryContent
        // that rebuilds the panel. Detaches the leaving beat's gain-routed hypno
        // audio deterministically, so no _videoAddedThisRefresh / scene-change
        // ordering race can skip it (the class that made the hypno-audio leak recur
        // ~10×). The new beat re-attaches its own clip later in _addVideo.
        events.emit(Events.STORY_STOP_MEDIA);

        // E-03: Watchdog for stuck _inForcedEvent. If a forced event left
        // state without firing #return:done/abort (plain divert back to the
        // saved knot bypassing the JS return handler), the flag stays true
        // forever and the SMS/dating queue stops draining.
        //
        // Detect the specific "stuck" case: we're back at the saved knot but
        // the flag is still set. The legitimate return path goes through
        // _onReturnFromForcedEvent which gates on _forcedEventReturning, so
        // landing back at savedKnot with the flag still set AND not in the
        // return phase means a plain divert leaked the flag.
        //
        // Earlier versions checked `curKnot !== savedKnot` which fired on
        // every forced event because curKnot is always the event's target
        // knot during the event (e.g. SMS_Bully_A) and savedKnot is the
        // pre-event location (Apartment) — that drained tracking immediately
        // on every event start, dropping the var snapshot before the event
        // could complete. Fixed v0.2.6 (foundby player Day 15 v0.2.5.2).
        if (this._inForcedEvent && !this._forcedEventReturning && this._savedKnotPath) {
            try {
                const curPath = this.currentStory.state?.currentPathString || '';
                const curKnot = curPath.split('.')[0] || this.currentLocation || '';
                const savedKnot = (this._savedKnotPath || '').split('.')[0];

                // Track whether we've ever been at a knot OTHER than savedKnot
                // during this event. Pre-fix: a fresh startForcedEvent that
                // enters a knot equal to savedKnot (because Ink was already
                // there from a prior aborted event) immediately tripped the
                // equality check and self-aborted the new event before it
                // could run a single refresh. The "left and came back" semantic
                // is the true stuck case — re-entry without ever leaving is
                // not. (foundby:test forced-event-blackout-release.spec.js:86
                // 2026-05-04, second-event-after-abort scenario.)
                if (curKnot && savedKnot && curKnot !== savedKnot) {
                    this._forcedEventVisitedDifferent = true;
                }

                if (curKnot && savedKnot && curKnot === savedKnot && this._forcedEventVisitedDifferent) {
                    console.warn(`[ForcedEvent] Stuck flag drained at ${savedKnot} (event exited via plain divert without #return:done)`);
                    this._abortForcedEvent(true);
                    this._stuckCycles = 0;
                    this._forcedEventVisitedDifferent = false;
                }
                // F11: cycle-based fallback for sub-knot diverts. The top-level
                // equality check misses cases where a forced event lands in a
                // sub-knot of an unrelated location (e.g. Bar → SMS_X → BarHub
                // via plain divert). Count refreshView cycles spent in-event;
                // after 50 (≈ 50 player choices), force-abort regardless of
                // location. Real forced events drain in 1-3 cycles.
                this._stuckCycles = (this._stuckCycles || 0) + 1;
                if (this._stuckCycles > 50) {
                    console.warn(`[ForcedEvent] Stuck flag drained by cycle counter (${this._stuckCycles} cycles, last knot=${curKnot})`);
                    this._abortForcedEvent(true);
                    this._stuckCycles = 0;
                    this._forcedEventVisitedDifferent = false;
                }
            } catch (e) {
                console.warn('[ForcedEvent] Watchdog non-fatal:', e?.message);
            }
        } else {
            this._stuckCycles = 0;
            this._forcedEventVisitedDifferent = false;
        }

        if (saveBeforeRefresh && !this._inForcedEvent) {
            events.emit(Events.SAVE_STARTED);
        }

        // Block Ink variable observers during processing to prevent transient
        // stat values from corrupting the UI (e.g. overnight code temporarily
        // resets TimeSlot/Energy before restoring them)
        variableManager.blockObservers = true;

        // Scroll-append mode: only clear on forced occasions or location changes
        if (this._forceClearNext) {
            this._forceClearNext = false;
            this._clearContentView();
        } else if (this._suppressNextAppend) {
            // Undo restored DOM exactly to pre-choice state — skip the
            // append separator that would otherwise insert a stray divider.
            this._suppressNextAppend = false;
            this._preRefreshLocation = this.currentLocation;
        } else {
            // Keep old content, add separator for new section
            this._preRefreshLocation = this.currentLocation;
            events.emit(Events.STORY_APPEND);
        }
        this._processStoryContent().catch(err => {
            console.error('[StoryManager] Unhandled story error:', err);
            this.isProcessing = false;
            this.waitingForInput = false;
            // Ink save-incompatibility recovery (foundby:ScarletMemories,
            // 0.2.7 -> 0.2.7.1): an old save loaded into a recompiled story can
            // carry a callstack pointer that no longer resolves in the new
            // container layout (previousPointer.Resolve() null). The state is
            // then both unrenderable AND unsaveable ("Slot N did not save").
            // Re-enter the saved location to rebuild a clean, saveable
            // callstack: keeps stats / inventory / vars, loses only the exact
            // mid-scene position. Scarlet's manual workaround was load -> go
            // outside -> re-save, which is this reset done by hand.
            const _msg = String((err && err.message) || err || '');
            if (!this._pointerRecovered && /previousPointer|\.Resolve\(\) is (null|undefined)/i.test(_msg)) {
                this._pointerRecovered = true;
                const _loc = this.currentLocation || 'Apartment';
                console.warn(`[StoryManager] Invalid Ink pointer (recompiled save), recovering by re-entering ${_loc}`);
                try {
                    this._abortForcedEvent(true);
                    this.currentStory.ChoosePathString(_loc);
                    events.emit(Events.TOAST_REQUEST, 'Save repaired after the update. You may need to re-open the current scene.', 'toast-info');
                    // Defer the re-render so we are not re-entrant inside this
                    // refreshView's own _processStoryContent catch.
                    setTimeout(() => { try { this.refreshView(); } catch (e) {} }, 0);
                    return;
                } catch (e) {
                    console.error('[StoryManager] Location recovery failed, trying Apartment:', e && e.message);
                    try {
                        this.currentStory.ChoosePathString('Apartment');
                        setTimeout(() => { try { this.refreshView(); } catch (e2) {} }, 0);
                        return;
                    } catch (e2) { /* fall through to the notification */ }
                }
            }
            events.emit(Events.NOTIFICATION_REQUEST,
                'An unexpected error occurred. You can try saving your game or returning to the apartment.');
        }).finally(() => {
            // Unblock observers after ALL processing (success or error)
            variableManager.blockObservers = false;
            // Pull every observed var from Ink back into the JS shadow.
            // During processing observers were blocked, so any Ink mutation
            // (e.g. ~ is_dirty = false in overnight reset, ~ wearing_chastity
            // changes via Master scenes, etc.) was silently dropped from the
            // baseVar/flags maps. Without this sync, the auto-save fired by
            // STORY_CHOICES_READY captures the stale shadow, and on reload
            // pushVariablesToInk overwrites Ink's correct values with the
            // stale ones — symptom: wake up clean, reload, dirty again.
            variableManager.syncFromInk();
        });
    }

    /**
     * Select a choice by index. Handles special indices for recovery/restart.
     * @param {number} index - Choice index, or -1 (restart), -2 (apartment recovery)
     */
    selectChoice(index) {
        if (index === -1) {
            // Restart: emit event for frontend to handle
            events.emit(Events.GAME_NEW);
            return;
        }

        if (index === -2) {
            // Dead-end recovery: jump to Apartment.
            // CRITICAL: also abort any in-flight forced event. Without this, a
            // dead-end inside a forced event leaves _inForcedEvent stuck at
            // true forever — every later SMS/Dating/SetBonus forced event gets
            // queued (since _inForcedEvent is true) but the queue never drains
            // (since the drain condition is !_inForcedEvent). Player report
            // 0.2.5.4 Day 141 Patreon (foundby:VPS): "Can't progress
            // connections in grindr (late game)" — Dating_Kai_1 queued but
            // never fired, SetBonus_Bunny "Blocked nested forced event".
            console.log('[StoryManager] Dead-end recovery — jumping to Apartment');
            this._abortForcedEvent(true);
            try { this.currentStory.ChoosePathString('Apartment'); }
            catch (e) {
                console.error(`[StoryManager] Apartment recovery failed: ${e.message}`);
                events.emit(Events.GAME_NEW);
                return;
            }
            this.refreshView();
            return;
        }

        if (!this.currentStory) return;
        if (index < 0 || index >= this.currentStory.currentChoices.length) {
            console.warn(`[StoryManager] selectChoice(${index}) out of range (${this.currentStory.currentChoices.length} choices)`);
            // Reset waitingForInput so a stuck UI doesn't trap the player on
            // the next refreshView (which short-circuits when waitingForInput=true).
            this.waitingForInput = false;
            return;
        }

        // Undo-button: snapshot state BEFORE committing the choice. Skipped
        // when minigames carry local JS state Undo can't restore (bar
        // cocktail grid, streaming live session). Forced events ARE
        // snapshotted — flags travel with the snapshot.
        try {
            const choiceText = (this._lastChoices && this._lastChoices[index]?.text) || '';
            this._pushUndoSnapshot(choiceText);
        } catch (e) {
            console.warn('[StoryManager] undo snapshot failed:', e?.message);
        }

        // Pacing feedback (Option A, foundby:VPS 0.2.8.2 "can't gain
        // feminization"): if the chosen action promised Fem but the early-game
        // pacing phase-lock will fully zero it, surface a one-time explainer so
        // the (correctly) hidden +Fem badge doesn't read as a broken game.
        // tutorial-tips.js imports this module, so emit an event rather than
        // call showTutorialTip directly (avoids a circular import).
        try {
            const chosen = this._lastChoices && this._lastChoices[index];
            if (chosen && femGainPacingSuppressed(this.currentStory, chosen.femTag)) {
                events.emit(Events.PACING_GAIN_SUPPRESSED, 'fem');
            }
        } catch (e) { /* non-fatal feedback hint */ }

        // Force-cancel any pending processing so the new choice can proceed
        this.isProcessing = false;
        this.waitingForInput = false;

        this.currentStory.ChooseChoiceIndex(index);
        this.refreshView();
    }

    // ------------------------------------------------------------------
    // Undo Button — pre-choice snapshot ring buffer
    // ------------------------------------------------------------------

    /**
     * @returns {boolean} True when at least one snapshot is available AND
     *                    no minigame is blocking restore.
     */
    canUndo() {
        if (!this._undoStack.length) return false;
        if (this._isMinigameBlockingUndo()) return false;
        return true;
    }

    /**
     * @returns {number} Snapshots currently buffered (0..5)
     */
    undoStackSize() { return this._undoStack.length; }

    /**
     * Register a JS-side state provider for undo snapshots. Providers make
     * the snapshot payload save-equivalent: capture() returns a deep-copied
     * serializable state, restore(state) re-applies it. Keyed — re-registering
     * the same key replaces the provider (boot paths may run twice).
     * @param {{key:string, capture:function():any, restore:function(any):void}} provider
     */
    registerUndoStateProvider(provider) {
        if (!provider || !provider.key) return;
        this._undoStateProviders = this._undoStateProviders.filter(p => p.key !== provider.key);
        this._undoStateProviders.push(provider);
    }

    _isMinigameBlockingUndo() {
        try {
            // Bar minigame holds local cocktail/crate grid state in JS.
            if (typeof window !== 'undefined' && window._barMinigame
                && typeof window._barMinigame.isActive === 'function'
                && window._barMinigame.isActive()) return true;
            // Streaming UI holds live-session timers.
            if (typeof window !== 'undefined' && window._streamingUI
                && typeof window._streamingUI.isActive === 'function'
                && window._streamingUI.isActive()) return true;
        } catch (_) {}
        return false;
    }

    _pushUndoSnapshot(label) {
        if (!this.currentStory) return;
        if (this._isMinigameBlockingUndo()) return;
        let inkState = '';
        try { inkState = this.currentStory.state.toJson(); }
        catch (e) { console.warn('[StoryManager] state.toJson failed:', e?.message); return; }

        let varState = null;
        try { varState = variableManager.exportState(); } catch (_) {}

        // JS-manager state (inventory, phone) — keyed by provider so restore
        // stays correct even if registration order ever changes.
        const extState = {};
        for (const p of this._undoStateProviders) {
            try { extState[p.key] = p.capture(); }
            catch (e) { console.warn(`[StoryManager] undo capture '${p.key}' failed:`, e?.message); }
        }

        let domHTML = '';
        try {
            const el = document.getElementById('story-content');
            if (el) {
                // Snapshot WITHOUT the live choice grid / continue indicator.
                // innerHTML restore would re-insert them as listener-less
                // copies (web-modules.md: innerHTML orphans listeners) and the
                // player ends up clicking dead buttons while the real grid
                // re-renders elsewhere. refreshView re-renders the live grid
                // after undo anyway. Foundby VPS report 2026-06-04.
                const clone = el.cloneNode(true);
                clone.querySelectorAll('#choices-inline, .choices-grid, .continue-indicator, .scene-end-fleuron:last-of-type')
                    .forEach(n => n.remove());
                domHTML = clone.innerHTML;
            }
        } catch (_) {}

        this._undoStack.push({
            inkState,
            varState,
            extState,
            domHTML,
            label: (label || '').slice(0, 80),
            location: this.currentLocation,
            travelLocation: this.travelLocation,
            // Forced-event JS flags travel with the snapshot so undo into a
            // forced-event-mid-flight or out of one stays consistent.
            inForcedEvent: this._inForcedEvent,
            savedKnotPath: this._savedKnotPath,
            preEventVarSnapshot: this._preEventVarSnapshot,
            forcedEventReturning: this._forcedEventReturning,
        });

        // Cap at _undoMax — drop oldest.
        while (this._undoStack.length > this._undoMax) this._undoStack.shift();

        events.emit(Events.UNDO_STACK_CHANGED, { available: this._undoStack.length });
    }

    _clearUndoStack() {
        if (!this._undoStack.length) return;
        this._undoStack.length = 0;
        events.emit(Events.UNDO_STACK_CHANGED, { available: 0 });
    }

    /**
     * Restore the most recent snapshot. No-op if stack empty or minigame
     * active. After restore: rerenders choices, fires autosave on the
     * normal slot, emits UNDO_PERFORMED.
     */
    undoLastChoice() {
        if (!this.canUndo()) return;
        const snap = this._undoStack.pop();
        if (!snap || !snap.inkState) return;

        try { this.currentStory.state.LoadJson(snap.inkState); }
        catch (e) {
            console.error('[StoryManager] Undo LoadJson failed:', e?.message);
            return;
        }

        try { if (snap.varState) variableManager.importState(snap.varState); }
        catch (e) { console.warn('[StoryManager] Undo importState failed:', e?.message); }

        // Ink is the source of truth post-restore (rule #4 save-state-drift.md).
        try { variableManager.syncFromInk(); } catch (_) {}

        // Restore JS-manager state (inventory equips, phone threads) so the
        // managers rewind WITH Ink. Must run before refreshView(true) — the
        // autosave it triggers reads these managers via the save collectors.
        // Snapshots from before this feature (same-session stack) have no
        // extState — skip, matching the old behaviour.
        if (snap.extState) {
            for (const p of this._undoStateProviders) {
                const s = snap.extState[p.key];
                if (s === undefined) continue;
                try { p.restore(s); }
                catch (e) { console.warn(`[StoryManager] undo restore '${p.key}' failed:`, e?.message); }
            }
        }

        this.currentLocation = snap.location || '';
        this.travelLocation = snap.travelLocation || null;
        this._inForcedEvent = !!snap.inForcedEvent;
        this._savedKnotPath = snap.savedKnotPath || null;
        this._preEventVarSnapshot = snap.preEventVarSnapshot || null;
        this._forcedEventReturning = !!snap.forcedEventReturning;

        // DOM restore — innerHTML reset to pre-choice render.
        try {
            const el = document.getElementById('story-content');
            if (el) {
                el.innerHTML = snap.domHTML || '';
                // Defense in depth for snapshots captured before the
                // capture-side strip (same-session stack): kill any restored
                // dead-button grid so only the freshly rendered one exists.
                el.querySelectorAll('#choices-inline, .choices-grid, .continue-indicator')
                    .forEach(n => n.remove());
            }
        } catch (_) {}

        this.isProcessing = false;
        this.waitingForInput = false;
        this._suppressNextAppend = true;

        // refreshView will skip the Continue() loop (canContinue=false post-restore),
        // skip STORY_APPEND (suppressed flag), and re-render the restored choices.
        // saveBeforeRefresh=true → autosave overwrites slot with restored state.
        try { this.refreshView(true); }
        catch (e) { console.error('[StoryManager] Undo refreshView failed:', e?.message); }

        events.emit(Events.UNDO_PERFORMED, { remaining: this._undoStack.length });
        events.emit(Events.UNDO_STACK_CHANGED, { available: this._undoStack.length });
    }

    /**
     * Select a choice by hotkey number (1-based).
     * @param {number} number - 1-based choice number
     */
    selectChoiceByHotkey(number) {
        if (!this.currentStory || this.isProcessing || this.waitingForInput) return;
        if (number <= 0 || number > this.currentStory.currentChoices.length) return;

        // Block hotkeys for disabled choices
        if (this._lastChoices && number - 1 < this._lastChoices.length && !this._lastChoices[number - 1].isEnabled) {
            return;
        }

        this.selectChoice(number - 1);
    }

    /**
     * Change location via quick travel.
     * @param {string} location - Location key from Constants.Location
     */
    changeLocation(location) {
        if (this.isProcessing || !this.currentStory) return;

        const inkPath = LOCATION_TO_KNOT[location];
        if (!inkPath) {
            console.error(`[StoryManager] Unknown location: ${location}`);
            return;
        }

        this.travelLocation = location; // preserve original Location key for UI
        this.currentLocation = inkPath;
        this.currentStory.ChoosePathString(inkPath);
        this._forceClearNext = true; // Location change = full clear
        this.refreshView();
    }

    /**
     * Force-jump to a knot (debug tool).
     * @param {string} knot
     */
    forceJump(knot) {
        if (!this.currentStory) return;
        this.isProcessing = false;
        this.currentLocation = knot;
        this._forceClearNext = true;
        this.currentStory.ChoosePathString(knot);
        this.refreshView(false);
    }

    // ------------------------------------------------------------------
    // Wait-for-Input Pattern (shops/wardrobe)
    // ------------------------------------------------------------------

    /**
     * Signal that an overlay (shop/wardrobe) is waiting for input.
     * Story processing pauses until releaseInput() is called.
     */
    waitForInput() {
        this.waitingForInput = true;
    }

    /**
     * Signal that the overlay has closed. Resumes story processing
     * without clearing existing content.
     */
    releaseInput() {
        this.waitingForInput = false;
        this._continueFromWait();
    }

    // ------------------------------------------------------------------
    // Forced Events (SMS [goto:] system)
    // ------------------------------------------------------------------

    /**
     * Start a forced event: snapshot variables, jump to knot.
     * @param {string} knot - Ink knot to jump to
     */
    startForcedEvent(knot) {
        if (!this.currentStory) {
            console.warn(`[ForcedEvent] No story — cannot start '${knot}'`);
            return;
        }

        if (this._inForcedEvent) {
            console.warn(`[ForcedEvent] Blocked nested forced event '${knot}' — already in forced event`);
            return;
        }

        // Force-clear processing state in case an old async loop is still flagged
        this.waitingForInput = false;

        // Save actual Ink knot for re-entry (not #location: tag which may differ from knot name)
        try {
            const pathStr = this.currentStory.state.currentPathString;
            this._savedKnotPath = pathStr ? pathStr.split('.')[0] : this.currentLocation;
        } catch(e) {
            this._savedKnotPath = this.currentLocation;
        }

        // Snapshot all Ink variables
        this._preEventVarSnapshot = {};
        const varNames = this.currentStory.variablesState._globalVariables ||
                         this.currentStory.variablesState['_globalVariables'];

        // Use the inkjs iterator to get all variable names
        if (this.currentStory.variablesState.forEach) {
            this.currentStory.variablesState.forEach((varName, value) => {
                this._preEventVarSnapshot[varName] = value;
            });
        } else {
            // Fallback: iterate using the $ enumerator pattern
            for (const varName of this._enumerateInkVariables()) {
                try {
                    this._preEventVarSnapshot[varName] = this.currentStory.variablesState[varName];
                } catch(e) { /* skip */ }
            }
        }

        this._inForcedEvent = true;
        this.isProcessing = false;
        this._forceClearNext = true;
        // Reset watchdog state at the start of every forced event so a prior
        // event's "visited different knot" trail doesn't leak forward and
        // cause the next event to self-abort on its first refreshView.
        this._stuckCycles = 0;
        this._forcedEventVisitedDifferent = false;

        // Suppress autosave for the duration of the forced event.
        // TAG_RETURN (fired by the event's #return:done) clears the flag.
        events.emit(Events.FORCED_EVENT_STARTED, knot);

        try {
            this.currentStory.ChoosePathString(knot);
        } catch (e) {
            console.error(`[ForcedEvent] Invalid knot '${knot}': ${e.message}`);
            this._inForcedEvent = false;
            this._preEventVarSnapshot = null;
            return;
        }

        this.refreshView(false);
    }

    /**
     * Start a replay of a completed SMS scene (no stat changes persist).
     * @param {string} knot
     */
    startReplay(knot) {
        if (!this.currentStory || this._inForcedEvent) return;

        this.startForcedEvent(knot);

        // Set replay_mode (cleared on return)
        if (this.currentStory) {
            variableManager.blockObservers = true;
            try { this.currentStory.variablesState['replay_mode'] = true; }
            finally { variableManager.blockObservers = false; }
        }
    }

    // ------------------------------------------------------------------
    // Ink State Serialization
    // ------------------------------------------------------------------

    /**
     * Save the Ink story state as JSON string.
     * @returns {string}
     */
    saveInkState() {
        if (!this.currentStory) return '';
        return this.currentStory.state.toJson();
    }

    /**
     * Load Ink story state from JSON string.
     * @param {string} stateJson
     */
    loadInkState(stateJson) {
        if (!this.currentStory || !stateJson) return;
        this.currentStory.state.LoadJson(stateJson);
    }

    // ------------------------------------------------------------------
    // Private: Story Processing
    // ------------------------------------------------------------------

    /**
     * Main story processing loop. Continues the story, handles tags,
     * emits content events, and presents choices.
     */
    async _processStoryContent() {
        // Increment generation counter — any old async loop with a stale token will bail
        this._processGeneration = (this._processGeneration || 0) + 1;
        const myGeneration = this._processGeneration;

        this.isProcessing = true;
        // F28: flag read by variableManager.pushVariablesToInk() to warn
        // if something triggers a redundant push mid-continue (dev-mode only).
        this._inContinueCycle = true;

        // P0501: Track narration cards added THIS refresh.
        // Used to (a) suppress mid-process _clearContentView calls that would
        // wipe already-rendered cards, and (b) watchdog at end of processing
        // to detect "choices rendered but story-content empty" (player report
        // 2026-05-01 ES Velvet Lounge ApplyJob).
        this._cardsAddedThisRefresh = 0;
        // P0504-extension: did a #ui: tag fire this refresh? Used by the
        // deferred family-clear to decide whether to keep the just-set
        // ui_mode (Mall_Pharmacy → #ui:shop_pharmacy fires, deferred clear
        // would otherwise close the shop the moment it opened) vs let it
        // clear normally (no new #ui: → stale ui_mode shouldn't be preserved
        // and shouldn't poison the empty-panel watchdog either).
        this._uiTagFiredThisRefresh = false;

        // P0505: emit refresh-start so story-content can snapshot the children
        // present BEFORE this refresh started. The deferred family-clear (line
        // ~822) runs AFTER tag handlers have already added new wrappers (e.g.
        // #img: → .story-image-wrapper) for the new knot. Without a baseline
        // snapshot the clear would wipe those tag-added wrappers along with
        // the previous knot's content, causing the new knot's image to vanish.
        // Symptoms: porn browser cards instead of porn image+choices view
        // (PornSites → PornSissy click), Master Morning_Inspection #img:
        // wiped right after being added (foundby player 2026-05-05).
        events.emit(Events.STORY_REFRESH_START);

        try {
            let locationChangedDuringProcessing = false;
            // P0504 (foundby:Moralesss v0.2.5.6): defer the family-change clear.
            // Hub knots without head text (GymHub, Apartment, ApartmentComputer,
            // many sub-location hubs) used to wipe the previous knot's narration
            // on iter 1 (cards=0 path), then add nothing themselves, leaving an
            // empty story panel where only the inline choices were visible -
            // the "bottom panel covers everything" report. Mark the clear as
            // pending; only execute it when an actual card is about to render.
            // If no card materialises by end-of-loop we cancel the clear so the
            // previous narration stays as scrollback context.
            let pendingFamilyClear = null;

            while (this.currentStory.canContinue && !this.waitingForInput) {
                // Bail if a newer processStoryContent was started
                if (this._processGeneration !== myGeneration) return;

                const text = this.currentStory.Continue();

                // Track current knot for forced event return
                if (!this._inForcedEvent) {
                    const path = this.currentStory.state.currentPathString;
                    if (path) {
                        this.currentLocation = path.split('.')[0];
                    }
                }

                // Detect location change — clear content for fresh start.
                // Knots in the same "family" (e.g. all Prologue_* knots) count
                // as one logical location so the scroll-append history isn't
                // wiped between adjacent narrative beats. Otherwise the player
                // can't scroll back to read the previous scene — the cleared
                // story panel only shows the new knot's content (Batl.leess
                // 2026-04-25: "can only see half the screen, can't scroll up
                // in the intro" — bottom of LongNight_Phone visible, prior
                // prologue knots wiped on every transition).
                if (this._preRefreshLocation && _locationFamily(this.currentLocation) !== _locationFamily(this._preRefreshLocation) && !locationChangedDuringProcessing) {
                    locationChangedDuringProcessing = true;
                    if (this._cardsAddedThisRefresh > 0) {
                        // P0501: Don't wipe cards we just rendered. Legitimate
                        // location-change clears fire on iter 1 (before any
                        // _createContentView call). If the path is empty on
                        // iter 1 and only becomes non-empty after a card is
                        // rendered, this branch would otherwise erase that card.
                        console.warn(`[Story] Mid-process clear suppressed (path-driven): ${this._cardsAddedThisRefresh} cards already rendered for ${this.currentLocation} (preRefresh=${this._preRefreshLocation})`);
                    } else {
                        // P0504: defer instead of clearing now. See pendingFamilyClear above.
                        pendingFamilyClear = `path-driven ${this.currentLocation} preRefresh=${this._preRefreshLocation}`;
                    }
                }

                // Process tags — #location: tag is the most reliable location signal
                const prevLoc = this.currentLocation;
                this._handleTags(this.currentStory.currentTags);
                // Check again after tags (the #location: tag updates currentLocation)
                if (this._preRefreshLocation && _locationFamily(this.currentLocation) !== _locationFamily(prevLoc) && !locationChangedDuringProcessing) {
                    locationChangedDuringProcessing = true;
                    if (this._cardsAddedThisRefresh > 0) {
                        // Same guard as above: if a #location: tag fires AFTER
                        // some narration has been rendered (e.g. tag is on the
                        // 3rd line of the knot, not the 1st), we mustn't wipe
                        // the already-rendered cards.
                        console.warn(`[Story] Mid-process clear suppressed (tag-driven): ${this._cardsAddedThisRefresh} cards already rendered for ${this.currentLocation} (prevLoc=${prevLoc})`);
                    } else {
                        // P0504: defer; only clear if an actual card materialises later in the loop.
                        pendingFamilyClear = `tag-driven ${this.currentLocation} prevLoc=${prevLoc}`;
                    }
                }

                // Read and clear line ID (for localization)
                const lineId = tagHandler.currentLineId;
                tagHandler.clearLineId();

                // Forced event return may have replaced story state
                if (this._forcedEventReturning) {
                    return; // bail out, deferred refresh will re-enter
                }

                if (this.waitingForInput) {
                    return;
                }

                // Handle break delay
                if (this._pendingDelay > 0) {
                    events.emit(Events.STORY_TYPING_START);
                    await this._waitForDelay(this._pendingDelay);
                    events.emit(Events.STORY_TYPING_STOP);
                    this._pendingDelay = 0;
                }

                // Emit content (suppress SMS/MMS text — shown in phone overlay, not story)
                const trimmed = text ? text.trim() : '';
                if (trimmed && !tagHandler.suppressContent) {
                    // P0504: a real card is about to render. Execute the
                    // deferred family-clear NOW (before adding the card) so
                    // the new content lands in a fresh panel. Without this
                    // the new narration would append below the previous
                    // location's content with no visual reset.
                    if (pendingFamilyClear) {
                        // Pass keepUIMode ONLY if a fresh #ui: tag fired
                        // this refresh — the new knot's tag (e.g.
                        // #ui:shop_pharmacy) already opened a shop overlay
                        // and clearing ui_mode here would close it the
                        // moment it opened. Without a fresh tag we let the
                        // clear run normally so a stale ui_mode (e.g.
                        // 'char_creation' carried from earlier in the
                        // session) doesn't (a) keep an obsolete overlay
                        // alive or (b) trick the empty-panel watchdog into
                        // suppressing recovery on a legitimate empty state.
                        // P0505: preserve children added by tag handlers
                        // during this refresh (e.g. #img: → wrapper appended
                        // before this clear executed). Without this flag the
                        // clear wipes the new knot's just-added image because
                        // the tag fires before the trimmed-text trigger.
                        const clearOpts = {
                            preserveTagAddedContent: true,
                        };
                        if (this._uiTagFiredThisRefresh) clearOpts.keepUIMode = true;
                        this._clearContentView(clearOpts);
                        pendingFamilyClear = null;
                    }
                    this._createContentView(trimmed, lineId);
                    this._cardsAddedThisRefresh++;
                }
                tagHandler.suppressContent = false;
            }

            if (this.waitingForInput) return;

            // F04: Generation token check after the while loop. A newer
            // selectChoice() may have force-reset isProcessing and started a
            // fresh _processStoryContent. Bail before _createChoicesUI fires
            // STORY_CHOICES_READY with the OLD choice list — autosave + UI
            // would otherwise see a doubled emit.
            if (this._processGeneration !== myGeneration) return;

            // P0504: end-of-loop reached with a deferred clear that was never
            // executed. The new knot emitted zero cards (hub knots like
            // GymHub / Apartment / ApartmentComputer are choices-only). Do
            // NOT clear: keep previous narration as scrollback context. The
            // new choices will render inline below the existing content.
            // Without this, the player saw an empty story panel where only
            // the inline choices were visible — "bottom panel covers
            // everything" (foundby:Moralesss v0.2.5.6 ClinicDoctor_Intro_Exam,
            // also explains GymHub/Apartment/ApartmentComputer watchdog
            // false-positives). Breadcrumb still needs to reflect the new
            // location even though we're not clearing.
            if (pendingFamilyClear) {
                console.warn(`[Story] Family-clear deferred and cancelled (no new content): ${pendingFamilyClear}`);
                events.emit(Events.STORY_LOCATION_CHANGED);
                pendingFamilyClear = null;
            }

            // P0501 watchdog: choices ready but no narration cards rendered.
            // The prevention guards above suppress mid-process clears, but
            // other code paths could still wipe content (#bgm: handlers,
            // overlays, etc.). Log to bracket-bug telemetry so we know
            // when this fires in the wild even after the prevention.
            //
            // P0504: dampen false positives. The watchdog originally fired
            // for ANY refresh where 0 cards landed but choices did, which
            // includes legitimate UI-mode handovers — location-map knots
            // (GymHub, Apartment, ClinicHub) where LocationsUI replaces the
            // story panel with a hotspot grid; #ui:desktop / #ui:shop_*
            // overlays where the choices live in a custom interface. None
            // of these are bugs. Skip the warning when a UI mode tag
            // claimed this refresh, when LocationsUI is active, or when
            // pendingFamilyClear was already cancelled (hub-return case
            // where prev narration is intentionally kept as scrollback).
            try {
                const choiceCount = this.currentStory?.currentChoices?.length || 0;
                if (choiceCount > 0 && this._cardsAddedThisRefresh === 0) {
                    const inkUIMode = (() => {
                        try { return this.currentStory?.variablesState?.['ui_mode'] || ''; } catch(e) { return ''; }
                    })();
                    const locationsActive = !!(window._locations && window._locations._activeLocation);
                    const knot = this.currentStory?.state?.currentPathString || this.currentLocation || '(unknown)';
                    // pendingFamilyClear was set to null after the end-of-loop
                    // emit, so this signal differs from locationChangedDuringProcessing
                    // alone: it tells us whether the deferred-clear was actually
                    // cancelled (legitimate hub-return scrollback) vs. executed
                    // (cards came in — watchdog wouldn't fire then anyway).
                    const isHubReturn = !!locationChangedDuringProcessing;

                    // P0506 (foundby:Jay v0.2.5.7.4): defensive DOM check.
                    // The watchdog reads currentLocation to look up the
                    // narration cache, but currentLocation can lag behind
                    // the actual knot when transitioning into a TEXT-FREE
                    // hub knot like Office_Exit (head: only `#info:info`
                    // / `#img:` / `#quicktravelpossible:`, no narration).
                    // In that case Continue() returns "" and
                    // state.currentPathString is null at choices-pending,
                    // so the path-tracking inside the loop never updates
                    // currentLocation away from the previous text-emitting
                    // knot. locationChangedDuringProcessing stays false →
                    // isHubReturn skip-guard misses → watchdog fires with
                    // the OLD knot's cache key → replays cards that are
                    // ALREADY in the panel (preserved by surgical clear or
                    // never cleared) → visible duplication.
                    //
                    // Defensive guard: regardless of the heuristic above,
                    // if the panel already has visible info-cards /
                    // dialogue-cards / image-wrappers, the panel is NOT
                    // empty and the recovery replay would be a duplication.
                    // Skip the replay.
                    let domHasVisibleCards = false;
                    try {
                        const sc = document.getElementById('story-content');
                        if (sc && sc.querySelector('.info-card, .dialogue-card, .story-image-wrapper')) {
                            domHasVisibleCards = true;
                        }
                    } catch(e) { /* SSR / no DOM — leave false */ }

                    // On a fresh app start (the player's "2nd launch") the save
                    // restores parked on a location-map hub's choices
                    // (OutsideChoices, GymHub, ClinicHub, ...). LocationsUI hasn't
                    // activated yet — it does so on the STORY_CHOICES_READY that
                    // _createChoicesUI emits AFTER this block — so `locationsActive`
                    // is still false and a map hub (whose narration lives in its
                    // parent knot, not re-run on LoadJson) trips the watchdog with a
                    // spurious CHOICES_WITHOUT_NARRATION. Detect the map hub directly:
                    // any pending choice tagged `#hotspot:` means a map renders this
                    // refresh, which is a legitimate narration-free view. (Verified:
                    // save→reload parked at OutsideChoices renders the map fine; the
                    // warning was a red herring in Android bug-report captures —
                    // VPS Android 0.2.8.4.3 "apk can't start".)
                    let isLocationMapHub = false;
                    try {
                        isLocationMapHub = this.currentStory.currentChoices.some(
                            c => (c.tags || []).some(t => typeof t === 'string' && t.startsWith('hotspot:'))
                        );
                    } catch(e) { /* leave false */ }

                    if (inkUIMode || locationsActive || isHubReturn || domHasVisibleCards || isLocationMapHub) {
                        // Legitimate empty-narration refresh (UI-mode handover,
                        // location-map hub, intentional hub-return scrollback,
                        // or hub-knot-with-preserved-content).
                        // Don't pollute the bracket-bug buffer with these.
                    } else {
                        // P0504: Real bug condition. Choices are about to render
                        // but nothing was emitted this refresh. Before we just
                        // log + telemetry, check if we have a cached narration
                        // for currentLocation that we can replay (forced-event-
                        // return path leaves Ink past the narration even after
                        // ChoosePathString re-entry; see VelvetLounge_ApplyJob
                        // report 2026-05-03 Moralesss). Replay is visual-only
                        // (re-emits STORY_INFO_TEXT/STORY_DIALOGUE), no Ink
                        // side effects.
                        const cached = this.currentLocation && this._narrationByKnot.get(this.currentLocation);
                        if (cached && cached.length > 0) {
                            console.warn(`[Story] EMPTY_PANEL_RECOVERY: replaying ${cached.length} cached narration card(s) for ${this.currentLocation} (knot=${knot})`);
                            this._narrationReplayInProgress = true;
                            try {
                                for (const card of cached) {
                                    if (card.type === 'info') {
                                        events.emit(Events.STORY_INFO_TEXT, card.text, card.lineId);
                                    } else if (card.type === 'dialogue') {
                                        events.emit(Events.STORY_DIALOGUE, card.displayName, card.text, card.portrait, card.lineId);
                                    } else if (card.type === 'image') {
                                        // story-content.js listens to TAG_IMAGE, not STORY_IMAGE
                                        // (orphan event since v0.2.x). _recordNarrationCard is
                                        // guarded by _narrationReplayInProgress so re-emit won't re-cache.
                                        events.emit(Events.TAG_IMAGE, card.value);
                                    } else if (card.type === 'video') {
                                        events.emit(Events.TAG_VIDEO, card.value);
                                    }
                                    this._cardsAddedThisRefresh++;
                                }
                            } finally {
                                this._narrationReplayInProgress = false;
                            }
                            if (!window.__bracketBugs) window.__bracketBugs = [];
                            if (window.__bracketBugs.length < 200) {
                                window.__bracketBugs.push({
                                    key: 'empty_panel_recovered',
                                    val: String(cached.length),
                                    knot,
                                    preRefresh: this._preRefreshLocation || '',
                                    ts: Date.now()
                                });
                            }
                        } else {
                            console.warn(`[Story] CHOICES_WITHOUT_NARRATION: ${choiceCount} choices ready but 0 narration cards added this refresh. Knot=${knot} preRefresh=${this._preRefreshLocation}`);
                            if (!window.__bracketBugs) window.__bracketBugs = [];
                            if (window.__bracketBugs.length < 200) {
                                window.__bracketBugs.push({
                                    key: 'choices_without_narration',
                                    val: String(choiceCount),
                                    knot,
                                    preRefresh: this._preRefreshLocation || '',
                                    ts: Date.now()
                                });
                            }
                        }
                    }
                }
            } catch(e) {
                // F08: empty-panel watchdog crashed. Log instead of swallow —
                // we want to know if the recovery path itself fails.
                console.warn('[Story] empty-panel watchdog crashed (non-fatal):', e?.message || e);
            }

            // Present choices
            // F04: re-check generation before each significant side-effect
            // because each call below can synchronously emit events.
            if (this._processGeneration !== myGeneration) return;
            this._createChoicesUI();
            if (this._processGeneration !== myGeneration) return;
            this._preloadChoiceImages();
            if (this._processGeneration !== myGeneration) return;
            events.emit(Events.STORY_CONTENT_FINISHED);

        } catch(e) {
            console.error('[Story] Processing error:', e);
            this.waitingForInput = false;

            // Notify user visibly
            events.emit(Events.NOTIFICATION_REQUEST,
                'Story processing error — your progress is safe. Try choosing an option or saving.');

            // Try to show current choices so the player can recover
            try {
                if (this.currentStory && this.currentStory.currentChoices.length > 0) {
                    this._createChoicesUI();
                } else {
                    // No choices available — offer Apartment recovery
                    const recoveryList = [{
                        index: -2,
                        text: 'Return to Apartment (error recovery)',
                        isEnabled: true,
                        isRestart: false,
                    }];
                    this._lastChoices = recoveryList;
                    events.emit(Events.STORY_CHOICES_READY, recoveryList);
                }
            } catch(e2) {
                console.error('[Story] Recovery also failed:', e2);
            }

        } finally {
            this.isProcessing = false;
            this._inContinueCycle = false;
        }
    }

    /**
     * Resume story processing after a wait-for-input overlay closes.
     */
    async _continueFromWait() {
        if (this.isProcessing || !this.currentStory) return;

        // F04 generation token — mirror _processStoryContent's race protection.
        // Pre-fix, _continueFromWait used the inner loop without bumping the
        // generation. A rapid overlay-close → selectChoice sequence could leave
        // an _continueFromWait awaiting _waitForDelay while a new selectChoice
        // started _processStoryContent. Both wrote to shared state concurrently.
        // (Audit 2026-05-23.)
        this._processGeneration = (this._processGeneration || 0) + 1;
        const myGeneration = this._processGeneration;

        this.isProcessing = true;
        try {
            this._pendingDelay = 0;
            await this._processStoryContentInner(myGeneration);
        } finally {
            if (this._processGeneration === myGeneration) {
                this.isProcessing = false;
            }
        }
    }

    /**
     * Inner processing loop (shared by processStoryContent and continueFromWait).
     * @param {number} [myGeneration] — caller's generation token; if provided,
     *   loop bails when _processGeneration advances (mirror of outer
     *   _processStoryContent guards).
     */
    async _processStoryContentInner(myGeneration) {
        try {
            while (this.currentStory.canContinue && !this.waitingForInput) {
                if (myGeneration !== undefined && this._processGeneration !== myGeneration) return;
                const text = this.currentStory.Continue();

                if (!this._inForcedEvent) {
                    const path = this.currentStory.state.currentPathString;
                    if (path) this.currentLocation = path.split('.')[0];
                }

                this._handleTags(this.currentStory.currentTags);
                const lineId = tagHandler.currentLineId;
                tagHandler.clearLineId();

                if (this._forcedEventReturning) return;
                if (this.waitingForInput) return;

                if (this._pendingDelay > 0) {
                    events.emit(Events.STORY_TYPING_START);
                    await this._waitForDelay(this._pendingDelay);
                    events.emit(Events.STORY_TYPING_STOP);
                    this._pendingDelay = 0;
                }

                const trimmed = text ? text.trim() : '';
                if (trimmed && !tagHandler.suppressContent) {
                    this._createContentView(trimmed, lineId);
                    if (typeof this._cardsAddedThisRefresh === 'number') this._cardsAddedThisRefresh++;
                }
                tagHandler.suppressContent = false;
            }

            if (!this.waitingForInput) {
                this._createChoicesUI();
                events.emit(Events.STORY_CONTENT_FINISHED);
            }
        } catch(e) {
            console.error('[Story] Processing error (continueFromWait):', e);
            this.waitingForInput = false;

            events.emit(Events.NOTIFICATION_REQUEST,
                'Story processing error — your progress is safe. Try choosing an option or saving.');

            // Try to show current choices so the player can recover
            try {
                if (this.currentStory && this.currentStory.currentChoices.length > 0) {
                    this._createChoicesUI();
                } else {
                    const recoveryList = [{
                        index: -2,
                        text: 'Return to Apartment (error recovery)',
                        isEnabled: true,
                        isRestart: false,
                    }];
                    this._lastChoices = recoveryList;
                    events.emit(Events.STORY_CHOICES_READY, recoveryList);
                }
            } catch(e2) {
                console.error('[Story] Recovery also failed:', e2);
            }
        }
    }

    /**
     * Wait for a delay (break tag). Returns a promise that resolves after the delay.
     * @param {number} seconds
     * @returns {Promise}
     */
    _waitForDelay(seconds) {
        // F29: cancelable timer. Pre-fix the setTimeout could not be cleared
        // when a new generation took over, so #break: pauses leaked across
        // generations and resolved against stale state.
        return new Promise(resolve => {
            // Clear any leftover delay from a prior aborted generation.
            if (this._delayTimer) { try { clearTimeout(this._delayTimer); } catch(_) {} }
            this._delayResolver = resolve;
            this._delayTimer = setTimeout(() => {
                this._delayTimer = null;
                this._delayResolver = null;
                resolve();
            }, seconds * 1000);
        });
    }

    _cancelPendingDelay() {
        if (this._delayTimer) {
            try { clearTimeout(this._delayTimer); } catch(_) {}
            this._delayTimer = null;
        }
        this._delayResolver = null;
    }

    /** @returns {boolean} true while a #break: timer is active. */
    get hasPendingDelay() { return this._delayTimer !== null; }

    /**
     * Resolve the active #break: delay immediately. Player-driven skip
     * (Space / scroll-wheel down). No-op when no delay is in flight.
     */
    skipPendingDelay() {
        if (this._delayTimer) {
            try { clearTimeout(this._delayTimer); } catch(_) {}
            this._delayTimer = null;
        }
        const r = this._delayResolver;
        this._delayResolver = null;
        if (r) r();
    }

    // ------------------------------------------------------------------
    // Private: Content Emission
    // ------------------------------------------------------------------

    _createContentView(text, lineId) {
        if (this._currentSpeaker === 'info') {
            events.emit(Events.STORY_INFO_TEXT, text, lineId);
            this._recordNarrationCard({ type: 'info', speaker: 'info', text, lineId });
        } else {
            // Resolve speaker base key (strip emotion suffix: master_smirk → master)
            const baseKey = this._currentSpeaker.split('_')[0];
            let displayName = tagHandler.resolveSpeakerName(this._currentSpeaker);

            // Resolve dynamic names (null in SPEAKER_DISPLAY_NAMES means runtime lookup)
            if (displayName === null || baseKey === 'master') {
                displayName = variableManager.getValue('MasterName') || 'Master';
            }
            if (baseKey === 'player') {
                displayName = variableManager.getValue('PlayerName') || 'You';
            }
            if (baseKey === 'girlfriend') {
                displayName = variableManager.getValue('GirlfriendName') || 'Sarah';
            }

            events.emit(Events.STORY_DIALOGUE, displayName, text, this._currentPortrait, lineId);
            this._recordNarrationCard({ type: 'dialogue', speaker: this._currentSpeaker, displayName, portrait: this._currentPortrait, text, lineId });
            // Reset speaker after dialogue line (back to narrator)
            this._currentSpeaker = 'info';
        }
    }

    /**
     * P0504: Record a rendered narration card under the current knot. Used by
     * the empty-panel watchdog to replay narration when story-content ends up
     * empty + choices ready (forced-event-return path bug). Cap per-knot at
     * 20 cards to bound memory.
     */
    _recordNarrationCard(card) {
        if (this._narrationReplayInProgress) return; // don't re-record during replay
        const knot = this.currentLocation || '';
        if (!knot) return;
        // F12: LRU semantics on the per-knot Map. Re-insert moves the knot
        // to the end (most-recently-used). Pre-fix the Map grew unbounded
        // over a long playthrough — every visited knot kept its 20-card
        // ring forever. 30-knot cap keeps recovery scope realistic.
        if (this._narrationByKnot.has(knot)) {
            const existing = this._narrationByKnot.get(knot);
            this._narrationByKnot.delete(knot);
            this._narrationByKnot.set(knot, existing);
        } else {
            this._narrationByKnot.set(knot, []);
        }
        const arr = this._narrationByKnot.get(knot);
        arr.push(card);
        if (arr.length > 20) arr.shift();
        // Evict oldest knot when over cap.
        if (this._narrationByKnot.size > 30) {
            const oldestKey = this._narrationByKnot.keys().next().value;
            this._narrationByKnot.delete(oldestKey);
        }
    }

    _clearContentView(opts) {
        this._currentSpeaker = 'info';
        this._currentPortrait = '';
        // Don't wipe _narrationByKnot here — we need it for empty-panel
        // recovery. Stale entries get capped at 20 per knot and replaced
        // when the player legitimately re-enters and renders fresh cards.
        // P0504-extension: deferred family-clear must NOT also reset
        // ui_mode. The new knot's tag block already fired #ui:shop_pharmacy
        // (or similar) on this same refresh; if STORY_CLEAR's tags-listener
        // calls clearUIMode() right after, the shop closes the moment it
        // opened. Symptom (Day 2 Mall_Pharmacy report 2026-05-04): "Apotheke
        // geht nicht" — text and 2 buttons render but the shop interface
        // never stays. Same class for Mall_Lingerie, Mall_AdultStore,
        // Mall_Clothing entry (and any future #ui:shop_* knot reachable
        // across a family boundary).
        events.emit(Events.STORY_CLEAR, opts || null);
    }

    _handleTags(tags) {
        if (!tags || tags.length === 0) return;
        for (const tag of tags) {
            // #location:Outside — explicit location tag, most reliable
            if (tag.startsWith('location:')) {
                this.currentLocation = tag.substring(9).trim();
            }
            // #bgm: — track as fallback location hint
            if (tag.startsWith('bgm:')) {
                this.lastBGM = tag.substring(4).trim();
            }
        }
        tagHandler.handleTags(tags);
    }

    _onCharacterTag(value) {
        this._currentPortrait = value;
        this._currentSpeaker = value === 'info' ? 'info' : value;
    }

    // ------------------------------------------------------------------
    // Private: Choice Building
    // ------------------------------------------------------------------

    /**
     * Build choice data objects from current Ink choices and emit STORY_CHOICES_READY.
     * Mirrors Unity InkManager.CreateChoicesUI() including stat previews.
     */
    _createChoicesUI() {
        // Sync the JS shadow state (variableManager.baseVar / .flags) from Ink
        // BEFORE STORY_CHOICES_READY fires. Auto-save listens on that event and
        // takes a synchronous snapshot of variableManager.exportState(); without
        // this sync it would capture the pre-processing shadow (observers were
        // blocked during _processStoryContent), persisting stale flags like
        // is_dirty=true that Ink had already cleared. The .finally in refreshView
        // also re-syncs as a safety net for paths that bypass _createChoicesUI.
        variableManager.syncFromInk();

        const inkChoices = this.currentStory.currentChoices;

        // Update currentLocation from choice sourcePath (most reliable after diverts,
        // since currentPathString is null when choices are pending)
        if (inkChoices.length > 0 && inkChoices[0].sourcePath) {
            const choiceKnot = inkChoices[0].sourcePath.split('.')[0];
            if (choiceKnot && choiceKnot !== this.currentLocation) {
                this.currentLocation = choiceKnot;
            }
        }

        if (inkChoices.length === 0) {
            // Dead-end recovery
            const path = this.currentStory.state.currentPathString || '';
            console.warn(`[StoryManager] Dead-end at '${path}' — offering Apartment recovery`);
            const recoveryList = [{
                index: -2,
                text: 'Return to Apartment',
                isEnabled: true,
                isRestart: false,
            }];
            this._lastChoices = recoveryList;
            events.emit(Events.STORY_CHOICES_READY, recoveryList);
            return;
        }

        // Cache trait values
        let trait1 = '', trait2 = '';
        try { trait1 = this.currentStory.variablesState['trait_1'] || ''; } catch(e) {}
        try { trait2 = this.currentStory.variablesState['trait_2'] || ''; } catch(e) {}

        const choices = [];

        for (let i = 0; i < inkChoices.length; i++) {
            const inkChoice = inkChoices[i];
            const data = {
                index: i,
                text: inkChoice.text.trim(),
                originalText: inkChoice.text.trim(),
                isEnabled: true,
                isRestart: false,
                wouldExhaust: false,
                wouldAutoSleep: false,
                disabledReason: null,
                energyCost: -1,
                timeCost: -1,
                arousalPenalty: 0,
                comfortDiscount: 0,
                arousalGain: -1,
                arousalTag: -1,
                femGain: -1,
                femTag: -1,
                subGain: -1,
                subTag: -1,
                shameGain: -1,
                shameTag: -1,
                isDramatic: false,
                locId: null,
                color: null,
                choiceImg: null,
            };

            // Process choice tags
            if (inkChoice.tags && inkChoice.tags.length > 0) {
                const parsed = tagHandler.parseChoiceTags(inkChoice.tags);

                data.isDramatic = parsed.isDramatic;
                data.color = parsed.color;
                data.locId = parsed.locId;
                if (parsed.choiceImg) data.choiceImg = parsed.choiceImg;
                if (parsed.hotspot) data.hotspot = parsed.hotspot;

                if (parsed.energyCost >= 0) data.energyCost = parsed.energyCost;
                if (parsed.timeCost >= 0) data.timeCost = parsed.timeCost;
                if (parsed.arousalGain !== -1) { data.arousalGain = parsed.arousalGain; data.arousalTag = parsed.arousalGain; }
                if (parsed.femGain !== -1) { data.femGain = parsed.femGain; data.femTag = parsed.femGain; }
                if (parsed.subGain !== -1) { data.subGain = parsed.subGain; data.subTag = parsed.subGain; }
                if (parsed.shameGain !== -1) { data.shameGain = parsed.shameGain; data.shameTag = parsed.shameGain; }

                // #disabled: tag
                if (parsed.isDisabled) {
                    data.isEnabled = false;
                    data.disabledReason = parsed.disabledReason || null;
                }

                // #req: checks (stat >= threshold required)
                // Positive-framed format: appends a `"${N} more"` tease when
                // the player is within 10 of the threshold, otherwise just
                // shows the gate. The in-character reason is preserved.
                for (const req of parsed.reqChecks) {
                    const current = variableManager.getTotalValue(req.stat);
                    if (current < req.threshold) {
                        data.isEnabled = false;
                        const gap = req.threshold - current;
                        const statShort = _shortStatLabel(req.stat);
                        const progressHint = gap <= 10
                            ? `${statShort} ${current} \u2192 ${req.threshold} (${gap} more)`
                            : `${statShort} ${req.threshold} needed`;
                        const line = `${req.reason} \u00b7 ${progressHint}`;
                        data.disabledReason = data.disabledReason
                            ? data.disabledReason + '\n' + line
                            : line;
                    }
                }

                // #max: checks (stat < threshold required)
                // Same positive framing inverted: appends how far over they
                // are, so "3 too many" reads more useful than "22/20".
                for (const max of parsed.maxChecks) {
                    const current = variableManager.getTotalValue(max.stat);
                    if (current >= max.threshold) {
                        data.isEnabled = false;
                        const over = current - max.threshold + 1;
                        const statShort = _shortStatLabel(max.stat);
                        const progressHint = over <= 10
                            ? `${statShort} ${current} \u2192 < ${max.threshold} (${over} too many)`
                            : `${statShort} must be below ${max.threshold}`;
                        const line = `${max.reason} \u00b7 ${progressHint}`;
                        data.disabledReason = data.disabledReason
                            ? data.disabledReason + '\n' + line
                            : line;
                    }
                }
            }

            // Stat previews for actions with energy cost
            if (data.isEnabled && data.energyCost > 0) {
                const currentEnergy = variableManager.getValue(Stats.ENERGY);
                const arousalPenalty = getArousalPenalty(this.currentStory);
                const comfortDiscount = getComfortDiscount(data.energyCost, this.currentStory);

                // Read signed Comfort so we capture the NEGATIVE-comfort
                // penalty side too. do_action uses `energy - Comfort` with
                // signed Comfort, so the preview must match. The existing
                // getComfortDiscount helper only surfaces the bonus (positive
                // Comfort); we compute a separate comfortPenalty for the
                // negative-Comfort case. F95 anon 2026-05-19: late-game
                // gear stack drove Comfort to -7 but the choice badge kept
                // advertising the base cost, then do_action charged the
                // inflated number — "+E tax" reads as a bug.
                let comfortRaw = 0;
                try { comfortRaw = this.currentStory.variablesState['Comfort'] || 0; }
                catch (e) { /* ignore */ }
                const comfortPenalty = Math.max(0, -comfortRaw);

                // HRT mood swing 3 (irritable): +2 energy cost in do_action
                let moodExtra = 0;
                try {
                    if (this.currentStory.variablesState['hrt_mood_swing'] === 3) moodExtra = 2;
                } catch (e) { /* ignore */ }

                // adjusted_energy_cost in do_action floors at 3 after Comfort/mood
                let adjusted = data.energyCost - comfortDiscount + comfortPenalty + moodExtra;
                if (adjusted < 3) adjusted = 3;

                // Drunk: numbness, discount up to 5 from raw cost
                let drunkDiscount = 0;
                try {
                    if (this.currentStory.variablesState['is_drunk']) {
                        const rawCost = adjusted + arousalPenalty;
                        drunkDiscount = Math.min(rawCost, 5);
                    }
                } catch (e) { /* ignore */ }

                const totalCost = adjusted + arousalPenalty - drunkDiscount;
                data.arousalPenalty = arousalPenalty;
                data.comfortDiscount = comfortDiscount;
                data.comfortPenalty = comfortPenalty;
                data.moodExtra = moodExtra;
                data.drunkDiscount = drunkDiscount;
                data.effectiveEnergyCost = totalCost;
                if (currentEnergy - totalCost <= 0) {
                    data.wouldExhaust = true;
                }

                // Add passive arousal from do_action()
                const passiveArousal = getPassiveArousalGain(this.currentStory);
                if (passiveArousal > 0) {
                    data.arousalGain = (data.arousalGain === -1 ? 0 : data.arousalGain) + passiveArousal;
                }

                // Apply diminishing returns to #sub: tag (+ Pacing-Phase-Wrapper)
                if (data.subTag !== -1) {
                    data.subGain = getEffectiveSubGain(data.subTag, this.currentStory);
                }
                const passiveSub = getPassiveSubGain(this.currentStory);
                if (passiveSub > 0) {
                    data.subGain = (data.subGain === -1 ? 0 : data.subGain) + passiveSub;
                }

                // Apply diminishing returns to #fem: tag
                if (data.femTag !== -1) {
                    data.femGain = getEffectiveFemGain(data.femTag, this.currentStory);
                }

                // Apply trait modifiers to #shame: tag (+ Pacing-Phase-Wrapper)
                if (data.shameTag !== -1) {
                    data.shameGain = getEffectiveShameGain(data.shameTag, this.currentStory);
                }
            }

            // Auto-sleep warning
            if (data.isEnabled && data.timeCost > 0) {
                const currentSlot = variableManager.getValue('TimeSlot');
                if (currentSlot >= 3) {
                    data.wouldAutoSleep = true;
                }
            }

            // Build human-readable cost text for hub-map hotspots and mobile
            // location buttons. Read by locations.js on every render — without
            // this string, hub hotspots silently dropped their cost preview
            // (caught 2026-04-29 during the prevention-pass audit; field was
            // referenced 12 times in locations.js but never populated since
            // the refactor). The compact-glyph format keeps the pin button small.
            //
            // Use effectiveEnergyCost so the pin reflects the same value the
            // player will pay after Arousal penalty / Comfort discount / HRT
            // mood / drunk numbness. Showing the base #energy:N tag would
            // re-introduce the v0.2.5.1 "energy goes faster than expected"
            // report — the action narrates 5 cost but Arousal 67 silently
            // adds +5, so the player paid 10 without warning.
            const costParts = [];
            const displayEnergyCost = (typeof data.effectiveEnergyCost === 'number' && data.effectiveEnergyCost !== data.energyCost)
                ? data.effectiveEnergyCost
                : data.energyCost;
            if (displayEnergyCost > 0) costParts.push(`⚡${displayEnergyCost}`);
            if (data.timeCost > 0) costParts.push(`⏱${data.timeCost}`);
            data.costText = costParts.join(' ');

            choices.push(data);
        }

        // Safety: if ALL choices disabled, add Apartment recovery
        if (choices.length > 0 && choices.every(c => !c.isEnabled)) {
            console.warn(`[StoryManager] All ${choices.length} choices disabled — adding Apartment recovery`);
            choices.push({
                index: -2,
                text: 'Return to Apartment',
                isEnabled: true,
                isRestart: false,
            });
        }

        // L10n: translate choice text via #id: tag if language != English.
        // SAFETY: never replace c.text with empty/null — if a key resolves to
        // empty (missing or blank in TSV), keep the original Ink text so the
        // choice never goes invisible. The L.get() chain already falls back
        // to EN, but defensive check prevents future regressions.
        if (L.getLanguage() !== 'en') {
            for (const c of choices) {
                if (!c.locId) continue;
                let interp = L.translateLine(c.locId, c.originalText);
                // Defensive: older TSV exports left the Ink choice-bracket `[` in
                // the translated text (e.g. `[Continue` without closing `]`).
                // Strip a leading `[` when the value has no matching `]`.
                if (interp && interp.startsWith('[') && !interp.includes(']')) {
                    interp = interp.slice(1).trimStart();
                }
                if (interp && interp.trim() && interp !== c.originalText) c.text = interp;
            }
        }

        this._lastChoices = choices;
        const pathNow = this.currentStory?.state?.currentPathString || '?';

        events.emit(Events.STORY_CHOICES_READY, choices);
    }

    // ------------------------------------------------------------------
    // Private: Image Preloading
    // ------------------------------------------------------------------

    /**
     * Peek ahead into each choice path to find #img: tags, then emit preload events.
     * Uses Ink state snapshot/restore so the actual game state is unaffected.
     */
    _preloadChoiceImages() {
        if (!this.currentStory) return;
        const choices = this.currentStory.currentChoices;
        if (choices.length === 0) return;

        // Save state snapshot
        const stateJson = this.currentStory.state.toJson();
        const preloaded = new Set();

        // F15: cap continue-walk per-choice. Pre-fix walked 20 lines per
        // choice = up to 120 Continue() calls per refresh, each invoking
        // Ink externals (do_action, fem_gain etc.) just for image discovery.
        // Most knots emit their primary #img: within 5 lines; bail early
        // once we found one to keep mobile responsive.
        const PRELOAD_MAX_STEPS = 8;
        for (let i = 0; i < choices.length; i++) {
            try {
                this.currentStory.ChooseChoiceIndex(i);
                let steps = 0;
                let foundImg = false;
                while (this.currentStory.canContinue && steps < PRELOAD_MAX_STEPS && !foundImg) {
                    this.currentStory.Continue();
                    const tags = this.currentStory.currentTags || [];
                    for (const tag of tags) {
                        if (tag.startsWith('img:')) {
                            const imgName = tag.substring(4).trim();
                            if (imgName && !preloaded.has(imgName)) {
                                preloaded.add(imgName);
                            }
                            foundImg = true; // primary image found, stop walking this branch
                        }
                    }
                    steps++;
                }
            } catch(e) {
                // Some choice paths may call externals that fail — that's fine
            }
            // Restore state for next choice
            this.currentStory.state.LoadJson(stateJson);
        }

        // Emit preload event — storyContent listens and warms browser cache
        if (preloaded.size > 0) {
            events.emit(Events.IMAGE_PRELOAD, [...preloaded]);
        }
    }

    // ------------------------------------------------------------------
    // Private: Forced Event Return
    // ------------------------------------------------------------------

    /**
     * Return from a forced event: restore variables, re-apply diffs.
     * Triggered by #return:done tag.
     */
    _onReturnFromForcedEvent() {
        if (!this._inForcedEvent || !this.currentStory) {
            console.warn('[StoryManager] Return called but not in forced event');
            return;
        }

        this._forcedEventReturning = true;

        // Capture post-event variables
        const postEventVars = {};
        for (const varName of this._enumerateInkVariables()) {
            try {
                postEventVars[varName] = this.currentStory.variablesState[varName];
            } catch(e) { /* skip */ }
        }

        this.isProcessing = false;

        // Block observers during mass variable restore
        variableManager.blockObservers = true;
        try {
            // Check if this was a replay
            const wasReplay = postEventVars['replay_mode'] === true;

            // Restore all variables to pre-event values
            for (const [key, value] of Object.entries(this._preEventVarSnapshot)) {
                try { this.currentStory.variablesState[key] = value; } catch(e) { /* skip */ }
            }

            // Re-apply diffs ONLY for normal forced events (not replays)
            if (!wasReplay) {
                for (const [key, postValue] of Object.entries(postEventVars)) {
                    const preValue = this._preEventVarSnapshot[key];
                    if (preValue !== postValue) {
                        try { this.currentStory.variablesState[key] = postValue; } catch(e) { /* skip */ }
                    }
                }
            }
        } finally {
            variableManager.blockObservers = false;
        }

        // Re-enter the original knot
        if (this._savedKnotPath) {
            try {
                this.currentStory.ChoosePathString(this._savedKnotPath);
            } catch(e) {
                console.error(`[ForcedEvent] Return to '${this._savedKnotPath}' failed, falling back to Apartment:`, e.message);
                try { this.currentStory.ChoosePathString('Apartment'); } catch(e2) { /* last resort */ }
            }
        }

        // Always clear replay_mode
        try { this.currentStory.variablesState['replay_mode'] = false; } catch(e) { /* ignore */ }

        // Clean up
        this._savedKnotPath = null;
        this._preEventVarSnapshot = null;
        this._inForcedEvent = false;

        // Deferred refresh (next microtask, analogous to Unity's next-frame coroutine)
        this._forceClearNext = true;
        Promise.resolve().then(() => {
            this._forcedEventReturning = false;
            this.refreshView(false);
        });
    }

    /**
     * Abort a forced event: clear the flag without restoring variables or re-entering
     * the saved knot. Used when the story naturally flows OUT of the forced event
     * (e.g. Exhaustion_Blackout → ApartmentSleep_Forced) — the stat changes made
     * during the event should persist and the story should continue on its current
     * path, not snap back to the pre-event location.
     *
     * Without this, _inForcedEvent stays true forever → every subsequent SMS/dating
     * forced event piles up in the queue and never drains.
     *
     * v0.2.7 audit M3: `restoreSnapshot=true` rolls the pre-event variable snapshot
     * back over the current state. This is used by the abnormal-exit paths
     * (stuck-watchdog, cycle-counter, dead-end recovery) where the player is being
     * yanked out of an event that was never supposed to end on a plain divert —
     * any mid-event mutations were not part of the intended outcome. The natural-
     * exit path (`#return:abort`) keeps the default `false` so Exhaustion_Blackout
     * style flows preserve the in-event stat changes the story script paid for.
     *
     * @param {boolean} [restoreSnapshot=false] Roll pre-event vars back over current.
     */
    _abortForcedEvent(restoreSnapshot = false) {
        if (!this._inForcedEvent) return;
        // F21: if the aborted event was a replay (startReplay set replay_mode=true),
        // the flag stays set in Ink unless we clear it explicitly. Subsequent
        // story flow with replay_mode=true silently no-ops do_action and stat
        // gains. Clear unconditionally — replay_mode should always be false
        // outside an active replay.
        if (this.currentStory) {
            try { this.currentStory.variablesState['replay_mode'] = false; } catch(e) {}
        }
        if (restoreSnapshot && this._preEventVarSnapshot && this.currentStory) {
            // Mirror _onReturnFromForcedEvent's restore phase, minus the
            // diff-replay (we want pre-event values to win on abnormal abort).
            variableManager.blockObservers = true;
            try {
                for (const [key, value] of Object.entries(this._preEventVarSnapshot)) {
                    try { this.currentStory.variablesState[key] = value; } catch(e) { /* skip */ }
                }
            } finally {
                variableManager.blockObservers = false;
            }
        }
        this._savedKnotPath = null;
        this._preEventVarSnapshot = null;
        this._inForcedEvent = false;
        // _forcedEventReturning is set true by _onReturnFromForcedEvent and
        // only cleared in its trailing microtask. If abort fires inside that
        // window, the flag stays true forever and refreshView's saveBeforeRefresh
        // gate (line 428) silently skips autosave on the post-recovery path.
        this._forcedEventReturning = false;
        // Watchdog state — must reset here so a SECOND forced event starting
        // at the same Ink-knot the prior one was at doesn't trip the
        // "back at saved knot" check via stale state from the first event.
        this._stuckCycles = 0;
        this._forcedEventVisitedDifferent = false;
    }

    /**
     * Clear a STALE _inForcedEvent flag before a voluntary, player-initiated
     * forced event that originates from an OPEN phone/overlay.
     *
     * Invariant: the phone force-closes whenever a forced event renders
     * (FORCED_EVENT_STARTED → phone.close(), phone.js). So a player can only
     * reach a phone-app session / SMS-goto tap when NO forced event is actually
     * on screen. If _inForcedEvent is still true at that moment it is therefore
     * a leaked flag (a prior event exited via a plain divert without firing
     * #return:done/abort, so the JS return handler never ran). The normal
     * refreshView watchdog clears such flags, but a phone tap enqueues WITHOUT
     * a refreshView, so the stale flag would silently send the session to the
     * forced-event queue — it then only drains on the next morning's
     * STORY_CHOICES_READY ("doesn't play until end of day", foundby:Niya Weiss,
     * HypnoMind 0.2.9).
     *
     * Clears WITHOUT restoring the (stale) pre-event snapshot. No-op if the flag
     * is already false or the engine is mid-render. Returns true if it cleared.
     */
    clearStaleForcedEventFlag() {
        if (!this._inForcedEvent) return false;
        if (this.isProcessing) return false;   // don't yank a live render
        console.warn('[ForcedEvent] Cleared stale _inForcedEvent before a voluntary phone-app event (leaked flag, no event was rendering)');
        this._abortForcedEvent(false);
        return true;
    }

    // ------------------------------------------------------------------
    // Private: Ink Variable Enumeration
    // ------------------------------------------------------------------

    /**
     * Enumerate all Ink variable names from the current story.
     * Handles different inkjs API versions.
     * @returns {string[]}
     */
    _enumerateInkVariables() {
        const names = [];
        if (!this.currentStory) return names;

        const vs = this.currentStory.variablesState;

        // inkjs 2.x: variablesState has a $ enumerator or _globalVariables map
        if (vs._globalVariables) {
            // Map or plain object
            if (vs._globalVariables instanceof Map) {
                for (const key of vs._globalVariables.keys()) names.push(key);
            } else if (typeof vs._globalVariables === 'object') {
                for (const key of Object.keys(vs._globalVariables)) names.push(key);
            }
        }
        // inkjs 2.2+: use the $ iterator
        else if (typeof vs[Symbol.iterator] === 'function') {
            for (const [name] of vs) names.push(name);
        }
        // Fallback: use the jsonToken approach
        else if (vs.jsonToken) {
            for (const key of Object.keys(vs.jsonToken)) names.push(key);
        }

        if (names.length === 0) {
            // No branch matched the inkjs internal shape — pre-event snapshot
            // in startForcedEvent will be empty and the post-event diff-apply
            // can't restore stat changes. inkjs internal API likely shifted.
            console.warn('[StoryManager] _enumerateInkVariables: no branch matched variablesState — inkjs internal shape may have changed');
        }

        return names;
    }
}

/** @type {StoryManager} Singleton */
export const storyManager = new StoryManager();
